import BadgeIcon from '@mui/icons-material/Badge'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Title, usePermissions, useTranslate } from 'react-admin'
import { MarketingLeadRecord, marketingLeads2026 } from './leadData.generated'

const SITEADMIN_ROLE = 'siteadmin'

const buildSearchValue = (lead: MarketingLeadRecord) =>
  [
    lead.eventName,
    lead.fullName,
    lead.email,
    lead.company,
    lead.role,
    lead.city,
    lead.state,
    lead.country,
    lead.context,
    lead.notes,
  ]
    .join(' ')
    .toLowerCase()

const buildLocation = (lead: MarketingLeadRecord) =>
  [lead.city, lead.state, lead.country].filter(Boolean).join(', ')

const LeadsList = () => {
  const translate = useTranslate()
  const { permissions, isLoading } = usePermissions()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEventKey, setSelectedEventKey] = useState('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const eventCounts = useMemo(() => {
    const counts = new Map<string, { eventName: string; count: number }>()

    marketingLeads2026.forEach((lead) => {
      const current = counts.get(lead.eventKey)
      if (current) {
        current.count += 1
        return
      }

      counts.set(lead.eventKey, {
        eventName: lead.eventName,
        count: 1,
      })
    })

    return Array.from(counts.entries())
      .map(([eventKey, data]) => ({
        eventKey,
        eventName: data.eventName,
        count: data.count,
      }))
      .sort((left, right) => left.eventName.localeCompare(right.eventName))
  }, [])

  const uniqueEmailCount = useMemo(() => {
    const emails = new Set(
      marketingLeads2026
        .map((lead) => lead.email.toLowerCase())
        .filter((email) => email.length > 0),
    )

    return emails.size
  }, [])

  const filteredLeads = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()

    return marketingLeads2026.filter((lead) => {
      if (selectedEventKey !== 'all' && lead.eventKey !== selectedEventKey) {
        return false
      }

      if (!normalizedQuery) {
        return true
      }

      return buildSearchValue(lead).includes(normalizedQuery)
    })
  }, [searchQuery, selectedEventKey])

  const paginatedLeads = useMemo(
    () => filteredLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredLeads, page, rowsPerPage],
  )

  useEffect(() => {
    setPage(0)
  }, [searchQuery, selectedEventKey])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (permissions?.role !== SITEADMIN_ROLE) {
    return (
      <Card sx={{ m: 2 }}>
        <CardContent>
          <Alert severity="warning">{translate('pos.leads.access_denied')}</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title={translate('pos.leads.title')} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              {translate('pos.leads.total_records')}
            </Typography>
            <Typography variant="h5">{marketingLeads2026.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              {translate('pos.leads.unique_emails')}
            </Typography>
            <Typography variant="h5">{uniqueEmailCount}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              {translate('pos.leads.sources')}
            </Typography>
            <Typography variant="h5">{eventCounts.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              {translate('pos.leads.filtered_records')}
            </Typography>
            <Typography variant="h5">{filteredLeads.length}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 2 }}>
              <Typography variant="h5" gutterBottom>
                {translate('pos.leads.title')}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {translate('pos.leads.subtitle')}
              </Typography>
            </Box>
            <TextField
              label={translate('pos.search')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={translate('pos.leads.search_placeholder')}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 320 } }}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              color={selectedEventKey === 'all' ? 'primary' : 'default'}
              icon={<BadgeIcon />}
              label={`${translate('pos.leads.all_events')} (${marketingLeads2026.length})`}
              onClick={() => setSelectedEventKey('all')}
            />
            {eventCounts.map((event) => (
              <Chip
                key={event.eventKey}
                color={selectedEventKey === event.eventKey ? 'primary' : 'default'}
                label={`${event.eventName} (${event.count})`}
                onClick={() => setSelectedEventKey(event.eventKey)}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('resources.leads.fields.event')}</TableCell>
                <TableCell>{translate('resources.leads.fields.name')}</TableCell>
                <TableCell>{translate('resources.leads.fields.email')}</TableCell>
                <TableCell>{translate('resources.leads.fields.company')}</TableCell>
                <TableCell>{translate('resources.leads.fields.role')}</TableCell>
                <TableCell>{translate('resources.leads.fields.location')}</TableCell>
                <TableCell>{translate('resources.leads.fields.context')}</TableCell>
                <TableCell>{translate('resources.leads.fields.captured_at')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLeads.length > 0 ? (
                paginatedLeads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>{lead.eventName}</TableCell>
                    <TableCell>{lead.fullName || translate('pos.common.empty_data')}</TableCell>
                    <TableCell>{lead.email || translate('pos.common.empty_data')}</TableCell>
                    <TableCell>{lead.company || translate('pos.common.empty_data')}</TableCell>
                    <TableCell>
                      {lead.role || lead.ticketType || translate('pos.common.empty_data')}
                    </TableCell>
                    <TableCell>
                      {buildLocation(lead) || translate('pos.common.empty_data')}
                    </TableCell>
                    <TableCell>{lead.context || translate('pos.common.empty_data')}</TableCell>
                    <TableCell>{lead.scannedAt || translate('pos.common.empty_data')}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {translate('pos.leads.empty_state')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredLeads.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
    </Box>
  )
}

export default LeadsList
